import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import ItemOfferedToSellStatus from '@/components/ItemStatus/ItemOfferedToSellStatus'
import { EHintType } from '@/components/ItemStatus/ItemStatus.types'
import Modal from '@/components/Modal'
import { useChangeItemStatus } from '@/domains/items/hooks/itemDetails/changeItemStatus'
import { useGetDealById } from '@/domains/items/hooks/itemDetails/getDealById'
import { useMutationShowingErrors } from '@/hooks'
import { EItemStatusType, Item, ItemEvent } from '@/schemaTypes'

type OfferToSellModalProps = {
  open: boolean
  onClose: () => void
  item: Item
  onSuccess?: () => void
  onLoadingChange?: (loading: boolean) => void
}

const OfferToSellModal = ({
  open,
  onClose,
  item,
  onSuccess,
  onLoadingChange,
}: OfferToSellModalProps) => {
  const { t } = useTranslation()
  const [updateStatusLoading, setUpdateStatusLoading] = useState(false)

  const changeStatus = useMutationShowingErrors({
    mutation: useChangeItemStatus(),
    successMessage: `${t('item_status_updated_successfully')}!`,
  })

  const dealId = item?.dealsHistory?.[item.dealsHistory.length - 1]?.dealId
  const itemTitle =
    item?.dealsHistory?.[item.dealsHistory.length - 1]?.title ?? ''

  const { deal } = useGetDealById({
    variables: { dealId: dealId! },
    skip: !open || !dealId,
    fetchPolicy: 'cache-first',
  })

  const onChangeItemStatus = (newEvent: ItemEvent, _hint: EHintType) => {
    setUpdateStatusLoading(true)
    onLoadingChange?.(true)
    changeStatus({
      variables: {
        itemId: item._id,
        newEvent: {
          createdAt: newEvent.createdAt,
          event: newEvent.event,
          timestamp: newEvent.timestamp,
          meta: newEvent.meta,
        },
        isStatusAddedManually: true,
      },
    })
      .then(() => {
        onSuccess?.()
        onClose()
      })
      .finally(() => {
        setUpdateStatusLoading(false)
        onLoadingChange?.(false)
      })
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ className: 'w-full max-w-[700px] rounded-2xl' }}
    >
      <div className="px-5 pb-6 pt-3">
        <div className="mx-auto w-full max-w-[560px]">
          <div className="flex flex-col gap-1 border-b border-black/10 pb-3 pt-1">
            <div className="text-lg font-semibold text-neutral-900">
              {item.storageLabel}
            </div>
            {itemTitle && (
              <div className="text-sm text-neutral-500">{itemTitle}</div>
            )}
            <div className="flex items-baseline gap-2 text-xs uppercase tracking-widest text-neutral-400">
              <span>{t('status')}:</span>
              <span className="font-semibold text-neutral-800">
                {EItemStatusType.OfferedToSell}
              </span>
            </div>
          </div>
        </div>
        <div className="mx-auto w-full max-w-[560px] pt-4">
          <ItemOfferedToSellStatus
            allowEmpty
            variant="modal"
            item={item}
            deal={deal}
            onChangeItemStatus={onChangeItemStatus}
            updateStatusLoading={updateStatusLoading}
          />
        </div>
      </div>
    </Modal>
  )
}

export default OfferToSellModal
